import type { OverrideFilters } from "../connectors/CadenceFiltersEditor";
import type { ScheduleEntry } from "./types";

/**
 * How one schedule entry is read and written. Shared by the table-major editor and the
 * schedule-major sheet (SIM-3402) because SIM-3097 is what happens when the two copy each other:
 * a fix landed on one `writeOverrideFilters` and not its twin, and typecheck, lint and tests all
 * passed. One definition, two hosts.
 */

/** Known schedule params with typed controls. */
export const KNOWN_PARAMS: Record<
  string,
  { type: "select"; options: string[] } | { type: "number" }
> = {
  mode: { type: "select", options: ["full", "incremental"] },
  minStartIndex: { type: "number" },
  lookbackRows: { type: "number" },
  lookbackDays: { type: "number" },
};

export const KNOWN_PARAM_KEYS = Object.keys(KNOWN_PARAMS);

/**
 * Keys NOT rendered as generic params. `name` is the entry key; `overrides` and `params` are
 * containers owned by CadenceFiltersEditor and RequiredParamsEditor, which the generic editor
 * would stringify as `[object Object]`. `at` is retired: the resolver reads the schedule
 * definition alone, so one left on an entry is dead data — never offer it. SIM-1369, SIM-3380.
 */
const BASE_KEYS = new Set(["name", "overrides", "params", "at"]);

export function getExtraParams(entry: ScheduleEntry): [string, string][] {
  return Object.entries(entry)
    .filter(([k]) => !BASE_KEYS.has(k))
    .map(([k, v]) => [k, String(v)]);
}

export function applyOverrideFilters(
  entry: ScheduleEntry,
  nextFilters: OverrideFilters | undefined,
): ScheduleEntry {
  const next: ScheduleEntry = { ...entry };
  if (nextFilters === undefined) {
    delete next.overrides;
  } else {
    next.overrides = { filters: nextFilters };
  }
  return next;
}

export function readOverrideFilters(entry: ScheduleEntry | undefined): OverrideFilters | undefined {
  return (entry?.overrides as { filters?: OverrideFilters } | undefined)?.filters;
}

export function buildEntry(
  baseEntry: ScheduleEntry,
  tableName: string,
  params: [string, string][],
): ScheduleEntry {
  // Preserve `overrides` (Slice C), `params` (SIM-1369), and any other
  // non-generic keys not surfaced by the generic param editor.
  const preserved: ScheduleEntry = { name: tableName };
  if (baseEntry.overrides !== undefined) {
    preserved.overrides = baseEntry.overrides;
  }
  if (baseEntry.params !== undefined) {
    preserved.params = baseEntry.params;
  }
  for (const [k, v] of params) {
    const num = Number(v);
    (preserved as Record<string, unknown>)[k] = v !== "" && !isNaN(num) ? num : v;
  }
  return preserved;
}

/** The required params this entry has no value for — SIM-1369's runtime precondition. */
export function missingRequiredParams(
  entry: ScheduleEntry,
  requiredParams: string[] | undefined,
): string[] {
  const values = (entry.params ?? {}) as Record<string, string | number>;
  return (requiredParams ?? []).filter((key) => {
    const v = values[key];
    return v === undefined || v === null || v === "";
  });
}
